import { startTransition, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Icon } from "@iconify/react";
import { formatDate } from "medere-ui-framework";
import "./StudyOrderView.scss";
import { StudyOrder, StudyOrderStatus } from "../../interface/interface";

import { useSearchParams, useNavigate } from "react-router";
import NewStudyOrderView from "./NewStudyOrder/NewStudyOrderView";
import { EStudyOrderSteps } from "./StudyOrderEnums";

interface StudyOrderViewProps {
  loadOrders?: () => Promise<StudyOrder[]>;
  onNewOrder?: () => void;
  onBack?: () => void;
  onSeeAll?: () => void;
  onOpenOrder?: (order: StudyOrder) => void;
}

export default function StudyOrderView({
  loadOrders,
  onNewOrder,
  onBack,
  onSeeAll,
  onOpenOrder,
}: StudyOrderViewProps) {
  const [orders, setOrders] = useState<StudyOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const medereentityIDParam = searchParams.get("medereentityID");
  const hpsIdParam = searchParams.get("healthPatientServiceId");
  const professionalIdParam = searchParams.get("professionalId");
  const studyTypeParam = searchParams.get("studyType");
  const scopeToPatientParam = searchParams.get("scopeToPatient");
  const patientId = medereentityIDParam ? Number(medereentityIDParam) : undefined;
  const healthPatientServiceId = hpsIdParam ? Number(hpsIdParam) : undefined;

  const buildHistoryUrl = () => {
    const qs = new URLSearchParams();
    if (medereentityIDParam) qs.set("medereentityID", medereentityIDParam);
    if (hpsIdParam) qs.set("healthPatientServiceId", hpsIdParam);
    if (professionalIdParam) qs.set("professionalId", professionalIdParam);
    if (studyTypeParam) qs.set("studyType", studyTypeParam);
    if (scopeToPatientParam === "true") qs.set("scopeToPatient", "true");
    const query = qs.toString();
    return query ? `/studyorderhistoryview?${query}` : "/studyorderhistoryview";
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    if (!loadOrders) {
      if (mounted) {
        setOrders([]);
        setLoading(false);
      }
      return () => {
        mounted = false;
      };
    }

    loadOrders()
      .then(data => {
        if (!mounted) return;
        setOrders(data ?? []);
      })
      .catch(() => {
        if (!mounted) return;
        setError("No se pudieron cargar las órdenes.");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [loadOrders]);

  // El early return va después de todos los hooks: si medereentityID aparece en la URL sin
  // remontar el componente, cortar antes del useEffect dispara React error #300.
  if (patientId) {
    const initialPatient = {
      medereentity: patientId,
      healthPatientServiceId,
    };

    return (
      <div className="viewContainer">
        <NewStudyOrderView
          setActiveView={() => {}}
          setSubActiveView={() => {}}
          setPrevActiveView={() => {}}
          setPrevSubActiveView={() => {}}
          onExitFlow={() => {
            const target = buildHistoryUrl();
            startTransition(() => navigate(target, { replace: true }));
          }}
          initialSelectedPatient={initialPatient}
          initialStep={
            searchParams.get("mode") === "COPY_STUDY_ORDER"
              ? EStudyOrderSteps.PRESCRIPTIONSTEP
              : EStudyOrderSteps.PRESCRIPTIONSTEP
          }
        />
      </div>
    );
  }

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    const target = buildHistoryUrl();
    startTransition(() => navigate(target, { replace: true }));
  };

  const renderStatusPill = (status: StudyOrderStatus) => {
    const map: Record<
      StudyOrderStatus,
      { label: string; className: string }
    > = {
      ACTIVA: { label: "Activa", className: "status-pill active" },
      CONSUMIDA: { label: "Consumida", className: "status-pill consumed" },
      VENCIDA: { label: "Vencida", className: "status-pill expired" },
    };
    const cfg = map[status];
    return <span className={cfg.className}>{cfg.label}</span>;
  };

  return (
    <div className="viewContainer">
      <div className="recipe-view-container study-order-view">
        <div className="study-order-header">
          <h1>Orden de estudios médicos</h1>
        </div>
        <Button className="study-order-main-button" onClick={onNewOrder}>
          <span className="study-order-main-icon-left">
            <Icon icon="mdi:clipboard-plus-outline" />
          </span>
          <span className="study-order-main-label">NUEVA ORDEN</span>
          <span className="study-order-main-icon-right">
            <Icon icon="mdi:chevron-right" />
          </span>
        </Button>
        <div className="study-order-section-header">
          <h2>Órdenes previas</h2>
          <button
            type="button"
            className="study-order-link"
            onClick={onSeeAll}
          >
            Ver todas <Icon icon="mdi:chevron-right" />
          </button>
        </div>
        {loading && (<div className="study-order-empty muted">Cargando órdenes...</div>)}
        {!loading && error && (<div className="study-order-empty error">{error}</div>)}
        {!loading && !error && orders.length === 0 && (<div className="study-order-empty muted">No hay órdenes previas.</div>)}
        {!loading && !error && orders.length > 0 && (
          <div className="study-order-list">
            {orders.map(order => (
              <button
                key={order.id}
                type="button"
                className="study-order-card"
                onClick={() => onOpenOrder?.(order)}
              >
                <div className="study-order-card-header">
                  <span className="study-order-code">{order.code}</span>
                  {renderStatusPill(order.status)}
                </div>

                <div className="study-order-line">
                  <span className="study-order-speciality">
                    {order.speciality}
                  </span>
                  <span className="study-order-dot">•</span>
                  <span className="study-order-date">
                    {formatDate(order.date)}
                  </span>
                </div>

                <div className="study-order-description">
                  {order.description}
                </div>

                <div className="study-order-studies-count">
                  {order.studiesCount} estudio(s)
                </div>
              </button>
            ))}
          </div>
        )}
        <Button className="study-order-back-button" onClick={handleBack}>
          Volver
        </Button>
      </div>
    </div>
  );
}
